import { useState, useRef, useEffect } from 'react';

import { useMutation } from '@apollo/client';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as braze from '@braze/web-sdk';
import styled from '@emotion/styled';
import {
    BRAZE_EVENT,
    BRAZE_SUBSCRIPTION_GROUP,
    colors, formatPhone, heapTrack
} from '@modernanimal/common';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { useNavigate } from 'react-router-dom';

import { AUTHORIZE_PHONE, CREATE_CLIENT, VERIFY_CODE } from './clientOnboarding.v3.gql';
import { VerifyCodeMutation, CreateClientMutation, ClientBasicInfoFragment } from '../__generated__/graphql';
import InputField from '../components/InputField';
import PrimaryButton from '../components/PrimaryButton';
import { useBrazeContext } from '../context/BrazeContext';
import Fonts from '../fonts';
import { hasScheduledOrCompletedAppointment, BookingFlowScreenName, getLastProgressScreenName } from '../helpers';
import useWebBookingStore from '../store';
import { isNumeric } from '../utils';

type Props = {
    goToScreen: (screenName: BookingFlowScreenName)=> void;
    locationId?: string | null;
};

const PhoneVerification = ({ goToScreen, locationId }: Props) => {
    const {
        client, updateClient, patient, updatePatient, externalClinic, updateIsMembershipCreated, appointment,
    } = useWebBookingStore((state) => ({
        client: state.client,
        updateClient: state.setClient,
        patient: state.patient,
        updatePatient: state.setPatient,
        externalClinic: state.externalClinic,
        updateIsMembershipCreated: state.setIsMembershipCreated,
        appointment: state.appointment,
    }));
    const { phoneNumber } = client;
    const navigate = useNavigate();
    const isBrazeInitialized = useBrazeContext();

    const [verificationCode, setVerificationCode] = useState<(string | null)[]>([null, null, null, null]);
    const [isValidCode, setIsValidCode] = useState(true);
    const [hasResentCode, setHasResentCode] = useState(false);

    const firstInputRef = useRef<HTMLInputElement>(null);
    const secondInputRef = useRef<HTMLInputElement>(null);
    const thirdInputRef = useRef<HTMLInputElement>(null);
    const fourthInputRef = useRef<HTMLInputElement>(null);
    const inputRefs = [firstInputRef, secondInputRef, thirdInputRef, fourthInputRef];
    const [authorizePhone] = useMutation(AUTHORIZE_PHONE);
    const [createClient] = useMutation(CREATE_CLIENT);
    const [verifyCode, { loading }] = useMutation(VERIFY_CODE);

    const setSentryUser = (client: ClientBasicInfoFragment) => {
        Sentry.setUser({
            id: client.id,
            email: client?.email || undefined,
            username: client.fullName
        });
    };

    useEffect(() => {
        const formattedVerificationCode = verificationCode.join('');
        if (formattedVerificationCode.length === 4) {
            const authorizeCode = async () => {
                await verifyCode({
                    variables: { phoneNumber: client.phoneNumber, verificationCode: formattedVerificationCode },
                    onCompleted: async (data) => {
                        const { client, ok } = data.authorizePhone;
                        if (hasScheduledOrCompletedAppointment(client)) {
                            if (ok && client) {
                                setSentryUser(client);
                            }
                            navigate('/download-app');
                        } else if (!ok) {
                            setIsValidCode(false);
                            if (hasResentCode) setHasResentCode(false);
                        } else {
                            await handleNext(client);
                        }
                    },
                    onError: (error) => {
                        setIsValidCode(false);
                        if (hasResentCode) setHasResentCode(false);
                        Sentry.captureException(error, {
                            extra: {
                                clientId: client.id,
                                path: 'src/clientOnboarding/PhoneVerification',
                                function: 'authorizeCode'
                            }
                        });
                    }
                });
            };

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            authorizeCode();
        } else if (formattedVerificationCode.length === 0 && !isValidCode) {
            setIsValidCode(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verificationCode, client.phoneNumber]);

    const changeInputFocus = (index: number, verificationKey: string) => {
        if (verificationKey.length > 0 && index < 3) {
            inputRefs[index + 1].current?.focus();
        } else if (verificationKey.length === 0 && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    const changeVerificationCode = (index: number) => (verificationKey: string) => {
        if (verificationKey !== '' && !isNumeric(verificationKey)) return;

        setVerificationCode((prevVerificationCode) => {
            const updatedVerificationCode = [...prevVerificationCode];
            updatedVerificationCode[index] = verificationKey.length ? verificationKey : null;
            return updatedVerificationCode;
        });

        changeInputFocus(index, verificationKey);
    };

    const getOrCreateClient = async (
        existingClient: VerifyCodeMutation['authorizePhone']['client'] | undefined,
        callback: (updatedClient: CreateClientMutation['createClient'])=> void
    ) => {
        if (!existingClient?.household.id) {
            await createClient({
                variables: { input: { phoneNumber } },
                onCompleted: (data) => {
                    callback(data.createClient);
                },
                onError: (error) => {
                    setIsValidCode(false);
                    if (hasResentCode) setHasResentCode(false);
                    Sentry.captureException(error, {
                        extra: {
                            function: 'getOrCreateClient',
                            path: 'src/clientOnboarding/PhoneVerification'
                        },
                    });
                }
            });
        } else {
            callback(existingClient);
        }
    };

    const handleNext = async (existingClient?: VerifyCodeMutation['authorizePhone']['client']) => {
        // eslint-disable-next-line complexity
        await getOrCreateClient(existingClient, (updatedClient) => {
            if ((existingClient?.household.patients.length ?? 0) > 0) {
                const existingPatient = existingClient?.household.patients[0];
                if (existingPatient) {
                    // Ensure existing patients are updated with the latest information
                    updatePatient({
                        ...patient,
                        id: existingPatient.id,
                        name: existingPatient.name,
                        species: existingPatient.species,
                        birthdate: existingPatient.birthdate,
                        isBirthdateKnown: !!existingPatient.birthdate,
                        breed: {
                            id: existingPatient.breedLookup.id,
                            name: existingPatient.breedLookup.name,
                            isMixed: existingPatient.isMixed
                        },
                        color: {
                            id: existingPatient.colorLookup.id,
                            name: existingPatient.colorLookup.name
                        },
                        sex: existingPatient.sex,
                        spayedOrNeutered: existingPatient.spayedOrNeutered,
                        // @ts-expect-error
                        membershipTier: existingPatient.tier,
                        needsRecords: existingPatient.needsRecords,
                        hasDeclaredNoPreviousRecords: existingPatient.hasDeclaredNoPreviousRecords
                    });
                    updateIsMembershipCreated(true);
                }
            }

            heapTrack('Web Booking - Code Verification View - Next Button Clicked');
            posthog.capture('web_booking_click', { screen: 'CodeVerification', action: 'NextButtonClicked' });

            if (updatedClient.id && updatedClient.token && updatedClient.household?.id) {
                updateClient({
                    ...client,
                    id: updatedClient.id,
                    token: updatedClient.token,
                    household: {
                        id: updatedClient.household?.id,
                        signupLocationId: appointment.slot?.location.id || locationId || '',
                        timezone: appointment.slot?.location.region?.timezone || ''
                    }
                });

                // identify user in PostHog
                // PH uses the household ID as the identifier for the user
                posthog.identify(updatedClient.household?.id, {
                    'Household ID': updatedClient.household?.id,
                    'Household Region ID': appointment.slot?.location.region?.id,
                    'Signup Location ID': appointment.slot?.location.id || locationId,
                });
                posthog.setPersonPropertiesForFlags({
                    'Household ID': updatedClient.household?.id,
                    'Household Region ID': appointment.slot?.location.region?.id,
                    'Signup Location ID': appointment.slot?.location.id || locationId,
                }, true);

                setSentryUser(updatedClient);

                if (isBrazeInitialized) {
                    // identify user in Braze
                    braze.changeUser(updatedClient.id);
                    braze.logCustomEvent(BRAZE_EVENT.ONBOARDING_START);
                    const brazeUser = braze.getUser();

                    if (brazeUser) {
                        brazeUser.addToSubscriptionGroup(BRAZE_SUBSCRIPTION_GROUP.MARKETING_COMMUNITY);
                        brazeUser.addToSubscriptionGroup(BRAZE_SUBSCRIPTION_GROUP.MARKETING_NEWSLETTERS);
                        brazeUser.addToSubscriptionGroup(BRAZE_SUBSCRIPTION_GROUP.MARKETING_PROMOTIONS);
                        brazeUser.addToSubscriptionGroup(BRAZE_SUBSCRIPTION_GROUP.TRANSACTIONAL_EDUCATION);
                        brazeUser.addToSubscriptionGroup(BRAZE_SUBSCRIPTION_GROUP.TRANSACTIONAL_REMINDERS);
                    }
                }

                const lastProgressScreenName = getLastProgressScreenName(client, patient, externalClinic);
                goToScreen(lastProgressScreenName);
            }
        });
    };

    const resendCode = async () => {
        await authorizePhone({
            variables: { phoneNumber: client.phoneNumber },
            onCompleted() {
                setHasResentCode(true);
                if (!isValidCode) setIsValidCode(true);
            },
            onError: (error) => {
                // TODO: We just have a more UI-friendly way to handle errors
                // eslint-disable-next-line no-alert
                alert(`Error! ${error}`);
                Sentry.captureException(error, {
                    extra: {
                        clientId: client.id,
                        function: 'resendCode',
                        path: 'src/clientOnboarding/PhoneVerification'
                    },
                });
            }
        });
    };

    const selectTextOnFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => e.target.select();

    return (
        <PhoneVerificationContainer>
            <Instructions>
                <Fonts.Heading04>
                    Enter your code
                </Fonts.Heading04>
                <Fonts.Body style={{ color: colors.GREY_2 }}>
                    {`We sent a one-time verification code to ${formatPhone(phoneNumber)} via SMS.`}
                </Fonts.Body>
            </Instructions>
            <InputAndMessage>
                <DigitInputContainer>
                    {inputRefs.map((inputRef, idx) => (
                        <InputField
                            // eslint-disable-next-line react/no-array-index-key
                            key={`phone-verification-input-${idx}`}
                            ref={inputRef}
                            value={verificationCode[idx] ?? ''}
                            onChange={changeVerificationCode(idx)}
                            maxLength={1}
                            autoFocus={idx === 0}
                            isInvalid={!isValidCode}
                            onKeyDown={(e) => {
                                if (e.key === 'Backspace' && !verificationCode[idx]) {
                                    // Add slight delay due to race condition where
                                    // input focuses then immediately triggers onChange of the
                                    // newly focused input. Causes previous input to be cleared too.
                                    setTimeout(() => changeInputFocus(idx, ''), 100);
                                }
                            }}
                            onFocus={selectTextOnFocus}
                            inputMode="numeric"
                            isTextCentered
                            dataTestId="phoneVerificationInput"
                        />
                    ))}
                </DigitInputContainer>
                {!isValidCode && (
                    <Fonts.BodySmall style={{ color: colors.RED }}>Invalid verification code</Fonts.BodySmall>
                )}
                {hasResentCode && (
                    <ResentBanner>
                        <Fonts.BodySmall style={{ color: colors.GREEN }}>
                            Re-sent verification code
                        </Fonts.BodySmall>
                    </ResentBanner>
                )}
            </InputAndMessage>
            <PrimaryButton onClick={resendCode} color="white" loading={loading}>
                Resend code
            </PrimaryButton>
        </PhoneVerificationContainer>
    );
};

export default PhoneVerification;

const PhoneVerificationContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 32px 24px;
    gap: 24px;
`;

const DigitInputContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const InputAndMessage = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Instructions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ResentBanner = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    background-color: ${colors.CONTAINER_GREEN};
    height: 36px;
`;
